import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import {Nullish} from 'shared/web';

import {createTemplateTokenSession} from '../templateTokens/createTemplateTokenSession';
import {renderTemplateTokens} from '../templateTokens/renderTemplateTokens';
import {TEMPLATE_TOKEN} from '../templateTokens/TEMPLATE_TOKEN';
import {RichTextTemplateTokens} from '../types';

/**
 * Token mode of a rich text editor, fixed on mount. When the labels of the placeholders in the
 * value change, the editor reloads the same canonical value through `reload`, which reports no
 * change. ReactQuill applies that reload as an `api` change, which Quill's history records like
 * any value loaded from outside: an undo right after it shows the placeholders as text again,
 * without a change to the canonical value.
 * A reload on a focused editor also moves the caret, because Quill resets the selection when it
 * replaces the content.
 */
export function useTemplateTokens(
  templateTokens: RichTextTemplateTokens | Nullish,
  canonicalValue: string,
  reload: (html: string) => void
) {
  const getLabelRef = useRef(templateTokens?.getLabel);
  // ReactQuill converts a new value in `shouldComponentUpdate`, before any effect could update the
  // ref. A render React discards can leave an uncommitted getter here; it reads only labels.
  getLabelRef.current = templateTokens?.getLabel;

  const [session] = useState(() =>
    isNil(templateTokens) ? null : createTemplateTokenSession((code) => getLabelRef.current?.(code))
  );

  const [emittedValue, setEmittedValue] = useState<string | null>(null);
  const currentValue = emittedValue ?? canonicalValue;
  const getLabel = templateTokens?.getLabel;
  // The rendered value reads the decoded text, as the editor does, so a placeholder spaced with
  // `&nbsp;` counts too.
  const rendered =
    isNil(session) || isNil(getLabel) ? '' : renderTemplateTokens(currentValue, getLabel);
  const [appliedRendered, setAppliedRendered] = useState(rendered);

  if (appliedRendered !== rendered) {
    setAppliedRendered(rendered);
    if (!isNil(getLabel)) {
      reload(rendered);
    }
  }

  return {
    session,
    formatName: isNil(session) ? null : TEMPLATE_TOKEN.blotName,
    /** Records a value the user produced, so neither its echo nor its labels reload the editor. */
    acceptUserValue: (value: string) => {
      setEmittedValue(value);
      if (!isNil(getLabel)) {
        setAppliedRendered(renderTemplateTokens(value, getLabel));
      }
    },
    /** Whether a new external value has to be loaded: false for the echo of a user value. */
    shouldLoadExternalValue: (value: string) => {
      if (!isNil(emittedValue) && value === emittedValue) {
        return false;
      }
      if (!isNil(emittedValue)) {
        setEmittedValue(null);
      }
      return true;
    },
  };
}
